import * as React from "react";
import { cn } from "@/lib/utils";

export interface ProductImageProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Product image URL
   */
  src: string;
  /**
   * Alt text for the image
   */
  alt: string;
  /**
   * Container layout classes (e.g., "w-full aspect-square", "flex-1 min-h-px")
   */
  containerClassName?: string;
}

/**
 * Reusable product image component with consistent styling
 *
 * Encapsulates the product image container with border, background, and image positioning.
 * Automatically handles missing/broken images with a placeholder fallback.
 * Accepts flexible container layout props to work in different contexts.
 *
 * @example
 * ```tsx
 * // Square aspect ratio (PublicCollectionItem style)
 * <ProductImage
 *   src="/images/jacket.jpg"
 *   alt="Jacket"
 *   containerClassName="w-full aspect-square relative"
 * />
 *
 * // With missing image (shows placeholder)
 * <ProductImage
 *   src=""
 *   alt="Product"
 *   containerClassName="w-full aspect-square relative"
 * />
 *
 * // Flex layout (PrivateCollectionItem style)
 * <ProductImage
 *   src="/images/jacket.jpg"
 *   alt="Jacket"
 *   containerClassName="flex-1 min-h-px min-w-px relative self-stretch shrink-0"
 * />
 * ```
 */
export const ProductImage = React.forwardRef<HTMLDivElement, ProductImageProps>(
  ({ src, alt, containerClassName, className, ...props }, ref) => {
    const [imageError, setImageError] = React.useState(false);
    const hasValidSrc = src && src.trim() !== '';

    // Reset error state when src changes
    React.useEffect(() => {
      setImageError(false);
    }, [src]);

    return (
      <div
        ref={ref}
        className={cn(
          // Common styling: border, radius, overflow
          "border border-gray-100 border-solid rounded-[0.3125rem] overflow-hidden",
          // Container layout (flexible for different use cases)
          containerClassName,
          className
        )}
        {...props}
      >
        {/* Background overlay and image container */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
        >
          {/* Light background */}
          <div className="absolute bg-gray-50 inset-0" />

          {/* Product image or placeholder */}
          {hasValidSrc && !imageError ? (
            <img
              alt={alt}
              className="absolute max-w-none object-center object-cover size-full"
              src={src}
              onError={() => setImageError(true)}
            />
          ) : (
            /* Placeholder for missing/broken images */
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <svg
                className="size-16 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
        </div>
      </div>
    );
  }
);

ProductImage.displayName = "ProductImage";
